import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Scale, Plus, Loader2, CheckCircle2, Clock, AlertTriangle, FileText, Database, Shield, List, Download } from 'lucide-react';
import { format } from 'date-fns';
import jsPDF from 'jspdf';

// ─── Types ───────────────────────────────────────────────────────────────────
type Framework = 'pdpl' | 'ndmo' | 'dga' | 'sdaia' | 'internal' | 'iso27001' | 'other';
type PolicyStatus = 'draft' | 'active' | 'under_review' | 'retired';

interface Policy {
  id: string;
  title: string;
  framework: Framework;
  status: PolicyStatus;
  owner_id: string | null;
  review_date: string | null;
  document_url: string | null;
  created_at: string;
}

interface DeploymentConfig {
  primary_region: string;
  primary_region_compliant: boolean;
  backup_region: string;
  backup_region_compliant: boolean;
  ai_processing_location: string;
  ai_processing_compliant: boolean;
  object_storage_location: string;
  object_storage_compliant: boolean;
  notes: string | null;
}

interface ConsentRecord {
  id: string;
  subject_email: string;
  purpose: string;
  granted: boolean;
  granted_at: string;
  expires_at: string | null;
  withdrawn_at: string | null;
}

// ─── Static PDPL/NDMO/DGA checklist ─────────────────────────────────────────
// pdplPrinciple maps each PDPL item to the underlying legal principle it
// satisfies (verified against public PDPL summaries — DLA Piper, PwC KSA
// Data Privacy Handbook, Akin — not the primary statute text). Deliberately
// does NOT cite specific article numbers: those need verification against
// the official PDPL text / SDAIA Implementing Regulations before use in an
// actual regulatory submission — a wrong citation here would be worse than
// none.
const CHECKLIST = [
  { id: 'pdpl-1', framework: 'PDPL',  item: 'Data classification policy documented', pdplPrinciple: 'Sensitive vs. ordinary Personal Data distinction — PDPL applies stricter controls to Sensitive Data (health, genetic, religious/political beliefs, race, ethnicity).' },
  { id: 'pdpl-2', framework: 'PDPL',  item: 'Consent mechanisms in place for personal data collection', pdplPrinciple: 'Lawful basis for processing — consent must be explicit, purpose-specific, and documented in a verifiable form; other lawful bases exist (contractual necessity, legal obligation, vital/legitimate interest).' },
  { id: 'pdpl-3', framework: 'PDPL',  item: 'Data retention schedules defined', pdplPrinciple: 'Processing-activity records must be retained 5 years after the relevant processing ceases.' },
  { id: 'pdpl-4', framework: 'PDPL',  item: 'Data subject rights procedures established (access, deletion)', pdplPrinciple: 'Data subject rights — access, correction, and deletion of personal data.' },
  { id: 'pdpl-5', framework: 'PDPL',  item: 'Cross-border data transfer controls implemented', pdplPrinciple: 'Cross-border transfers require SDAIA-approved Standard Contractual Clauses (or equivalent safeguards) and must not conflict with the Kingdom’s national interest.' },
  { id: 'ndmo-1', framework: 'NDMO',  item: 'Data catalog maintained and up-to-date' },
  { id: 'ndmo-2', framework: 'NDMO',  item: 'Data ownership assigned per domain' },
  { id: 'ndmo-3', framework: 'NDMO',  item: 'Data quality metrics tracked' },
  { id: 'ndmo-4', framework: 'NDMO',  item: 'Data lineage documented for critical datasets' },
  { id: 'dga-1',  framework: 'DGA',   item: 'Governance workflows mapped to DGA framework' },
  { id: 'dga-2',  framework: 'DGA',   item: 'Portfolio KPIs aligned to national strategy objectives' },
  { id: 'dga-3',  framework: 'DGA',   item: 'Benefits realization tracking in place' },
  { id: 'sdaia-1',framework: 'SDAIA', item: 'AI model registry up-to-date' },
  { id: 'sdaia-2',framework: 'SDAIA', item: 'HITL approval process documented' },
  { id: 'sdaia-3',framework: 'SDAIA', item: 'AI outputs subject to human review before publishing' },
  { id: 'sdaia-4',framework: 'SDAIA', item: 'AI incident log maintained' },
];

const FRAMEWORK_COLORS: Record<string, string> = {
  pdpl:    'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  ndmo:    'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  dga:     'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  sdaia:   'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  internal:'bg-muted text-muted-foreground',
  iso27001:'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  other:   'bg-muted text-muted-foreground',
};

const STATUS_COLORS: Record<PolicyStatus, string> = {
  active:       'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  draft:        'bg-muted text-muted-foreground',
  under_review: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  retired:      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

// ─── Component ───────────────────────────────────────────────────────────────
export const ComplianceCenter = () => {
  const { tenantId } = useAuth();
  const { toast } = useToast();

  const [policies, setPolicies] = useState<Policy[]>([]);
  const [consents, setConsents] = useState<ConsentRecord[]>([]);
  const [deploymentConfig, setDeploymentConfig] = useState<DeploymentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  const [policyDialog, setPolicyDialog] = useState(false);
  const [policyForm, setPolicyForm] = useState({ title: '', framework: 'internal' as Framework, status: 'draft' as PolicyStatus, review_date: '', document_url: '' });
  const [savingPolicy, setSavingPolicy] = useState(false);

  const [consentDialog, setConsentDialog] = useState(false);
  const [consentForm, setConsentForm] = useState({ subject_email: '', purpose: '', expires_at: '' });
  const [savingConsent, setSavingConsent] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [pRes, cRes, dRes] = await Promise.all([
      supabase.from('policies').select('*').order('created_at', { ascending: false }),
      supabase.from('consent_log').select('*').order('granted_at', { ascending: false }).limit(100),
      supabase.from('deployment_config').select('*').limit(1).maybeSingle(),
    ]);
    if (!pRes.error) setPolicies(pRes.data as Policy[]);
    if (!cRes.error) setConsents(cRes.data as ConsentRecord[]);
    if (!dRes.error) setDeploymentConfig(dRes.data as DeploymentConfig | null);

    // Load checklist state from localStorage (per tenant)
    const stored = localStorage.getItem(`compliance_checklist_${tenantId}`);
    if (stored) setChecked(JSON.parse(stored));
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const toggleCheck = (id: string) => {
    const next = { ...checked, [id]: !checked[id] };
    setChecked(next);
    localStorage.setItem(`compliance_checklist_${tenantId}`, JSON.stringify(next));
  };

  const handleSavePolicy = async () => {
    if (!policyForm.title.trim()) { toast({ title: 'Title required', variant: 'destructive' }); return; }
    setSavingPolicy(true);
    const { error } = await supabase.from('policies').insert({
      ...policyForm,
      title: policyForm.title.trim(),
      review_date: policyForm.review_date || null,
      document_url: policyForm.document_url || null,
      tenant_id: tenantId,
    });
    setSavingPolicy(false);
    if (error) { toast({ title: 'Save failed', description: error.message, variant: 'destructive' }); return; }
    toast({ title: 'Policy saved' });
    setPolicyDialog(false);
    setPolicyForm({ title: '', framework: 'internal', status: 'draft', review_date: '', document_url: '' });
    load();
  };

  const handleSaveConsent = async () => {
    if (!consentForm.subject_email || !consentForm.purpose) { toast({ title: 'Email and purpose required', variant: 'destructive' }); return; }
    setSavingConsent(true);
    const { error } = await supabase.from('consent_log').insert({
      ...consentForm,
      expires_at: consentForm.expires_at || null,
      granted: true,
      tenant_id: tenantId,
    });
    setSavingConsent(false);
    if (error) { toast({ title: 'Save failed', description: error.message, variant: 'destructive' }); return; }
    toast({ title: 'Consent recorded' });
    setConsentDialog(false);
    setConsentForm({ subject_email: '', purpose: '', expires_at: '' });
    load();
  };

  const checkedCount = Object.values(checked).filter(Boolean).length;
  const checklistScore = Math.round((checkedCount / CHECKLIST.length) * 100);

  const handleExportReport = () => {
    const pdf = new jsPDF('portrait', 'mm', 'a4');
    const marginX = 18;
    const pageWidth = pdf.internal.pageSize.getWidth();
    let y = 20;

    const addLine = (text: string, size = 10, bold = false) => {
      pdf.setFontSize(size);
      pdf.setFont('helvetica', bold ? 'bold' : 'normal');
      const lines = pdf.splitTextToSize(text, pageWidth - marginX * 2);
      pdf.text(lines, marginX, y);
      y += lines.length * (size * 0.42) + 3;
      if (y > 280) { pdf.addPage(); y = 20; }
    };

    addLine('Masira — Compliance Status Report', 16, true);
    addLine(`Generated ${format(new Date(), 'PPP p')}`, 9);
    y += 3;

    addLine(`Checklist completion: ${checkedCount}/${CHECKLIST.length} items (${checklistScore}%)`, 11, true);
    y += 2;

    for (const fw of ['PDPL', 'NDMO', 'DGA', 'SDAIA']) {
      addLine(fw, 12, true);
      for (const c of CHECKLIST.filter((c) => c.framework === fw)) {
        addLine(`${checked[c.id] ? '[x]' : '[ ]'} ${c.item}`, 10);
      }
      y += 2;
    }

    addLine(`Policy Registry (${policies.length} policies)`, 12, true);
    if (policies.length === 0) {
      addLine('No policies recorded.', 10);
    } else {
      for (const p of policies) {
        addLine(`${p.title} — ${p.framework.toUpperCase()} — ${p.status}${p.review_date ? ` — review by ${p.review_date}` : ''}`, 10);
      }
    }
    y += 2;

    addLine(`Consent Log (${consents.length} records shown, most recent 100)`, 12, true);
    if (consents.length === 0) {
      addLine('No consent records.', 10);
    } else {
      const grantedCount = consents.filter((c) => c.granted && !c.withdrawn_at).length;
      addLine(`${grantedCount} active, ${consents.length - grantedCount} withdrawn/revoked.`, 10);
    }

    addLine('This report reflects the checklist and records in this system at export time. PDPL principle references (checklist tab) are for internal guidance — verify exact article citations against the official PDPL text and SDAIA Implementing Regulations before regulatory submission.', 8);

    pdf.save(`Masira-Compliance-Report-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast({ title: 'Compliance report exported' });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Scale className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-2xl font-bold text-foreground">Compliance Center</h2>
            <p className="text-muted-foreground mt-0.5">PDPL · NDMO · DGA · SDAIA governance tracking</p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={handleExportReport}>
          <Download className="h-4 w-4" /> Export Report
        </Button>
      </div>

      {/* Score banner */}
      <Alert className="border-primary/30 bg-primary/5">
        <Shield className="h-4 w-4 text-primary" />
        <AlertDescription className="flex items-center justify-between">
          <span className="text-sm">Compliance checklist completion: <strong>{checkedCount}/{CHECKLIST.length}</strong> items</span>
          <span className={`text-lg font-bold ${checklistScore >= 80 ? 'text-emerald-600' : checklistScore >= 50 ? 'text-amber-600' : 'text-destructive'}`}>
            {checklistScore}%
          </span>
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="checklist">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="checklist" className="gap-2"><List className="h-4 w-4" />PDPL/NDMO Checklist</TabsTrigger>
          <TabsTrigger value="policies"  className="gap-2"><FileText className="h-4 w-4" />Policy Registry</TabsTrigger>
          <TabsTrigger value="consent"   className="gap-2"><Database className="h-4 w-4" />Consent Log</TabsTrigger>
          <TabsTrigger value="residency" className="gap-2"><Shield className="h-4 w-4" />Data Residency</TabsTrigger>
        </TabsList>

        {/* ── Checklist ── */}
        <TabsContent value="checklist" className="space-y-4 mt-4">
          {['PDPL', 'NDMO', 'DGA', 'SDAIA'].map(fw => (
            <Card key={fw}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${FRAMEWORK_COLORS[fw.toLowerCase()]}`}>{fw}</span>
                  {fw === 'PDPL'  && 'Personal Data Protection Law'}
                  {fw === 'NDMO'  && 'National Data Management Office'}
                  {fw === 'DGA'   && 'Digital Government Authority'}
                  {fw === 'SDAIA' && 'Saudi Data & AI Authority'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {CHECKLIST.filter(c => c.framework === fw).map(c => (
                  <label key={c.id} className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={!!checked[c.id]}
                      onChange={() => toggleCheck(c.id)}
                      className="mt-0.5 h-4 w-4 rounded border-border text-primary"
                    />
                    <span className="flex-1">
                      <span className={`text-sm block ${checked[c.id] ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                        {c.item}
                      </span>
                      {'pdplPrinciple' in c && c.pdplPrinciple && (
                        <span className="text-xs text-muted-foreground block mt-0.5">{c.pdplPrinciple}</span>
                      )}
                    </span>
                    {checked[c.id] && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />}
                  </label>
                ))}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ── Policies ── */}
        <TabsContent value="policies" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button size="sm" className="gap-2" onClick={() => setPolicyDialog(true)}>
              <Plus className="h-4 w-4" /> Add Policy
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              {policies.length === 0 ? (
                <div className="flex flex-col items-center py-12 text-muted-foreground">
                  <FileText className="h-8 w-8 mb-3 opacity-40" />
                  <p className="text-sm">No policies yet</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Policy</th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Framework</th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Status</th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Review Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {policies.map(p => (
                      <tr key={p.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-6 py-3 font-medium">{p.title}</td>
                        <td className="px-6 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium uppercase ${FRAMEWORK_COLORS[p.framework]}`}>{p.framework}</span>
                        </td>
                        <td className="px-6 py-3">
                          <Badge className={`text-xs ${STATUS_COLORS[p.status]}`}>{p.status.replace('_', ' ')}</Badge>
                        </td>
                        <td className="px-6 py-3 text-muted-foreground">
                          {p.review_date ? format(new Date(p.review_date), 'MMM d, yyyy') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Consent Log ── */}
        <TabsContent value="consent" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button size="sm" className="gap-2" onClick={() => setConsentDialog(true)}>
              <Plus className="h-4 w-4" /> Log Consent
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              {consents.length === 0 ? (
                <div className="flex flex-col items-center py-12 text-muted-foreground">
                  <Database className="h-8 w-8 mb-3 opacity-40" />
                  <p className="text-sm">No consent records yet</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Data Subject</th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Purpose</th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Status</th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Granted</th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Expires</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {consents.map(c => (
                      <tr key={c.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-6 py-3 font-medium">{c.subject_email}</td>
                        <td className="px-6 py-3 text-muted-foreground">{c.purpose}</td>
                        <td className="px-6 py-3">
                          {c.withdrawn_at ? (
                            <Badge variant="destructive" className="text-xs">Withdrawn</Badge>
                          ) : c.granted ? (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs"><CheckCircle2 className="h-3 w-3 me-1" />Active</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Declined</Badge>
                          )}
                        </td>
                        <td className="px-6 py-3 text-muted-foreground">{format(new Date(c.granted_at), 'MMM d, yyyy')}</td>
                        <td className="px-6 py-3 text-muted-foreground">
                          {c.expires_at ? format(new Date(c.expires_at), 'MMM d, yyyy') : 'No expiry'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Data Residency ── */}
        <TabsContent value="residency" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-5 w-5" /> Data Residency Configuration
              </CardTitle>
              <CardDescription>Configure where data is stored and processed in compliance with PDPL requirements</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!deploymentConfig ? (
                <p className="text-sm text-muted-foreground">Loading deployment facts…</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { label: 'Primary Region', value: deploymentConfig.primary_region, compliant: deploymentConfig.primary_region_compliant },
                      { label: 'Backup Region',  value: deploymentConfig.backup_region,   compliant: deploymentConfig.backup_region_compliant },
                      { label: 'AI Processing',  value: deploymentConfig.ai_processing_location, compliant: deploymentConfig.ai_processing_compliant },
                      { label: 'Object Storage', value: deploymentConfig.object_storage_location, compliant: deploymentConfig.object_storage_compliant },
                    ].map(r => (
                      <div key={r.label} className="flex items-center justify-between p-4 rounded-lg border">
                        <div>
                          <p className="text-sm font-medium">{r.label}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{r.value}</p>
                        </div>
                        {r.compliant ? (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs gap-1"><CheckCircle2 className="h-3 w-3" />Compliant</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 text-xs gap-1"><AlertTriangle className="h-3 w-3" />Review</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                  {deploymentConfig.notes && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription className="text-sm">{deploymentConfig.notes}</AlertDescription>
                    </Alert>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Policy dialog */}
      <Dialog open={policyDialog} onOpenChange={setPolicyDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Policy</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Title *</Label><Input value={policyForm.title} onChange={e => setPolicyForm(f => ({ ...f, title: e.target.value }))} placeholder="Policy title" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Framework</Label>
                <Select value={policyForm.framework} onValueChange={v => setPolicyForm(f => ({ ...f, framework: v as Framework }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdpl">PDPL</SelectItem>
                    <SelectItem value="ndmo">NDMO</SelectItem>
                    <SelectItem value="dga">DGA</SelectItem>
                    <SelectItem value="sdaia">SDAIA</SelectItem>
                    <SelectItem value="iso27001">ISO 27001</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={policyForm.status} onValueChange={v => setPolicyForm(f => ({ ...f, status: v as PolicyStatus }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="retired">Retired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Review Date</Label><Input type="date" value={policyForm.review_date} onChange={e => setPolicyForm(f => ({ ...f, review_date: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Document URL</Label><Input placeholder="https://…" value={policyForm.document_url} onChange={e => setPolicyForm(f => ({ ...f, document_url: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPolicyDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePolicy} disabled={savingPolicy}>{savingPolicy ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}Save Policy</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Consent dialog */}
      <Dialog open={consentDialog} onOpenChange={setConsentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Log Consent Record</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Data Subject Email *</Label><Input type="email" value={consentForm.subject_email} onChange={e => setConsentForm(f => ({ ...f, subject_email: e.target.value }))} placeholder="user@example.com" /></div>
            <div className="space-y-2"><Label>Purpose *</Label><Input value={consentForm.purpose} onChange={e => setConsentForm(f => ({ ...f, purpose: e.target.value }))} placeholder="e.g. Project analytics, AI-assisted reporting" /></div>
            <div className="space-y-2"><Label>Expiry Date (optional)</Label><Input type="date" value={consentForm.expires_at} onChange={e => setConsentForm(f => ({ ...f, expires_at: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConsentDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveConsent} disabled={savingConsent}>{savingConsent ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}Record Consent</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComplianceCenter;
